import { CancellationToken } from "../../../base/common/cancellation";
import { Event } from "../../../base/common/events";
import { IJSONSchema } from "../../../base/common/jsonSchema";
import { createDecorator } from "../../instantiation/common/registry";
import type { ContextKeyExpression } from "../../contextkey/common/contextkey";
import type { AiTarget, AnchorVia } from "./target";
import type { ChatDraft } from "./chat/chatDraft.js";


// #region AI Service

export type AIPersona = "chat" | ({} & string);

export const IAIService = createDecorator<IAIService>("aiService");

export type AIServiceChangedEvent = {
    readonly service: IAIService;
}
export interface IAIService {

    readonly onDidChange: Event<AIServiceChangedEvent>;

    readonly contextService: IAIContextService;
    readonly toolService: IAIToolService;

    /**
     * Get an AI system provider for the given persona.
     */
    getProvider(persona: AIPersona): IAISystemProvider | null;

    /**
     * Activate the given AI system provider.
     */
    registerProvider(provider: IAISystemProvider): Promise<void>;

}



export const enum AISystemProviderCapabilities {

    None = 0,

    TextGeneration = 1 << 0,
    TextGenerationStreaming = 1 << 1,

    ChatHistory = 1 << 2,

    /** One-shot completions ({@link IAISystemProvider.complete}) — a single
     * request/response with no chat session, history or tools. Distinct from
     * {@link TextGeneration}, which runs INSIDE the chat (visible in the panel,
     * part of the history). */
    Completions = 1 << 3,
    // ContextAwareness = 1 << 4,

    // ImageGeneration = 1 << 2,
    // ImageGenerationVariations = 1 << 3,

}



// #region AI System Provider

export interface AIChatMessage {
    role: "user" | "assistant" | "system";
    content: string;
    timestamp: number;

    reasoning?: string,
    refusal?: string | null,
    tool_calls?: Array<{
        /** Transport identity used to merge stream and local execution events. */
        callId?: string,
        toolName: string;
        arguments?: string,
        result?: string,
    }>;

    /** Optional actions rendered as clickable buttons in the chat UI. */
    actions?: Array<{ label: string, id: string }>;

    /** The user stopped this answer mid-stream — the kept partial must read
     *  as cut short, not as a finished thought. */
    stopped?: boolean;

    /**
     * How loudly a SYSTEM message should read. Decided where the failure is
     * known — a blocked turn and a friendly note are otherwise typographically
     * identical, and the UI must not have to sniff "Error:" out of the text.
     * Absent = an ordinary notice.
     */
    severity?: "error" | "warning";

    /**
     * Pictures belonging to this message — a screenshot the AI took, an image the
     * user attached. Width/height when the server knew them, so the UI can hold
     * the space before the image loads.
     */
    images?: Array<{ url: string, width?: number, height?: number }>;

    /**
     * This may be set to mark a chat message be generated by a tool call. It can be used to hide messages but keep them in history.
     */
    type?: "tool_response";
}

export interface AiSystemProviderDidChangeEvent {
    readonly provider: IAISystemProvider;
    finished?: boolean;
    error?: string;
}
export interface AISystemProviderCapabilitiesChangedEvent {
    readonly provider: IAISystemProvider;
}

export type GenerateOptions = {
    systemPrompt?: string;
    token?: CancellationToken;
    /**
     * Images the user attached to THIS message (pasted, dropped or picked).
     * Already downscaled in the browser — see platform/ai/browser/attachmentImages.
     */
    images?: Array<AIToolImage & { width?: number, height?: number }>;
}

/** A past chat session, e.g. for a recent-chats picker. */
export interface AIChatSessionSummary {
    /** Stable id of the chat on the backend. */
    slug: string;
    /** Human-readable label (server topic, else the slug). */
    label: string;
    /** Last-activity time (ms epoch) — most recent first when sorted. */
    updatedAt: number;
}

export interface IAISystemProvider {

    readonly persona: AIPersona;
    capabilities: AISystemProviderCapabilities;

    readonly onDidChange: Event<AiSystemProviderDidChangeEvent>;
    readonly onCapabilitiesChanged: Event<AISystemProviderCapabilitiesChangedEvent>;

    get isProcessing(): boolean;

    canGenerateText?(): Promise<boolean>;
    generateText?(prompt: string, options?: GenerateOptions): Promise<AIGenerateTextResponse>;
    generateTextStream?(prompt: string, options?: GenerateOptions): AsyncIterable<AIGenerateTextResponse>;

    /** ONE-SHOT completion: no chat session, no history, no tools, nothing in
     * the chat panel — for small background tasks (revision descriptions,
     * generated labels). Cloud-side this is POST /v1/ai/completions. */
    complete?(prompt: string, options?: AICompletionOptions): Promise<AIGenerateTextResponse>;

    /** One conversation's history — default: the chat on screen; a slug reads
     *  a background conversation WITHOUT switching to it. */
    getChatHistory?(chat?: string): Promise<AIChatMessage[]>;
    clearChatHistory?(): Promise<void>;

    // #region Chat sessions (multiple named chats per user/team)
    /** The slug of the currently active chat, if the provider is session-based. */
    readonly currentChatSlug?: string;
    /** Start a fresh chat session (new slug) and clear the local history. */
    startNewChat?(): Promise<void>;
    /** Recent chat sessions for the current user/team (unsorted; caller sorts). */
    listRecentChats?(): Promise<AIChatSessionSummary[]>;
    /** Switch to a past chat by slug and load its messages. */
    openChat?(slug: string): Promise<void>;
    /** Open the conversation ANCHORED to a thing — RESUMING its existing chat
     *  when this anchor was asked about before (per host), else starting a
     *  fresh one. Every turn carries an attribution + description block for
     *  the anchor (additive; the chat can still reach the whole scene).
     *  Returns the conversation's slug. `activate: false` (the bubble flow)
     *  leaves the main panel's chat, queue and reload target untouched. See
     *  plans/contextual-ask-ai.md. */
    openAnchoredChat?(anchor: AIChatAnchor, options?: { activate?: boolean }): Promise<string>;

    /** Re-pin an anchored conversation to a NEW anchor (#138): moves the
     *  anchor AND its entry in the anchor→slug map. Collision = the new
     *  anchor already has its own remembered chat — refused unless `force`
     *  (explicit user gestures force; the AI tool does not). */
    retargetAnchoredChat?(slug: string, anchor: AIChatAnchor, options?: { force?: boolean }): RetargetChatOutcome;

    /** The conversation's LIVE anchor — the bubble UI syncs from this on
     *  provider change so a retarget reaches the title, the follow rungs and
     *  the persisted record (the bubble's own copy would otherwise pin the
     *  OLD subject again on reload). */
    anchorOf?(slug: string): AIChatAnchor | null;

    /** What the model is given right now (the live provider chunks), as plain
     *  text — copied along with a conversation so it is actionable elsewhere. */
    getContextText?(): Promise<string>;

    /** Remember which anchored-chat BUBBLES are open, and where/how big they
     *  are — the whole list, per host, replacing what was there. */
    rememberOpenBubbles?(bubbles: readonly AIChatBubbleRecord[]): void;
    /** The bubbles open at the last unload, back-to-front (top-layer order is
     *  temporal, so the frontmost is LAST and must be shown last). Each one's
     *  conversation is re-created with its anchor re-attached; none of them
     *  becomes the active chat. */
    restoreOpenBubbles?(): AIChatBubbleRecord[];

    // #region Send queue (plans/ai-chat-concurrency.md §2)
    /** Send a message, or queue it behind that conversation's running turn —
     *  each conversation has its own queue and drains in order as its turns
     *  finish; different conversations stream independently. `chat` targets a
     *  conversation by slug (default: the one on screen). Resolves when this
     *  message's own turn completes. */
    sendOrQueue?(prompt: string, options?: GenerateOptions & { chat?: string }): Promise<void>;
    /** Stop one conversation's generation — default: the chat on screen (a
     *  bubble passes its own slug), never every chat. The partial answer stays
     *  in the history and the next queued message follows — "skip ahead", not
     *  "discard". */
    stop?(chat?: string): void;
    /** Messages waiting behind the running turn, oldest first (the chat on
     *  screen; per conversation via {@link queuedMessagesFor}). */
    readonly queuedMessages?: readonly AIQueuedMessage[];
    /** {@link queuedMessages} for ONE conversation by slug. */
    queuedMessagesFor?(chat?: string): readonly AIQueuedMessage[];
    /** Unsent text and images survive panel recreation; defaults to the active chat. */
    draftFor?(chat?: string): ChatDraft;
    /** Take a queued message BACK (to edit or discard) before it sends —
     *  returns its content, or null when it already drained or was removed.
     *  The sender's pending promise settles with a cancellation. */
    unqueueMessage?(id: number, chat?: string): Promise<AIQueuedMessage | null>;
    /** Whether ONE conversation is generating (default: the chat on screen) —
     *  what a chat surface binds its spinner/stop to; `isProcessing` stays
     *  global ("the AI is doing something, wherever"). */
    isChatProcessing?(chat?: string): boolean;
    // #endregion

    // generateImage?(prompt: string, options?: { width?: number; height?: number; count?: number }): Promise<string[]>;
}

/** A message waiting in the send queue — what the UI renders as pending. */
export type AIQueuedMessage = {
    /** stable identity for taking it back ({@link IAISystemProvider.unqueueMessage})
     *  — the drain shifts the queue under the UI, so an index would hit the
     *  wrong one */
    readonly id: number;
    readonly prompt: string;
    readonly images?: GenerateOptions["images"];
}

export type RetargetChatOutcome =
    | { code: "ok" }
    | { code: "not-anchored" }
    | { code: "collision"; existingSlug: string };

/**
 * What an anchored conversation is ABOUT (plans/contextual-ask-ai.md).
 * The anchor is an ADDRESS, never an instance — re-resolved at every use;
 * `label` carries the attribution when resolution fails ("stale, never break").
 */
export type AIChatAnchor = {
    /** the document target of the anchored thing */
    readonly target: AiTarget;
    /** display name — also the attribution when the target no longer resolves */
    readonly label: string;
    /** open-ended (same pattern as AIPersona): anything in a project can be an
     *  anchor — objects and resources first, files/properties/graph-nodes later */
    readonly kind: "object" | "resource" | "file" | "property" | "graph-node" | ({} & string);
    /** For an anchor whose OWN address cannot survive a reload — a geometry or
     *  material three.js made at runtime, whose id is minted into the live
     *  object — where it hangs: the holder and the slot. The holder is a node,
     *  and nodes resolve by path. See {@link AnchorVia}. */
    readonly via?: AnchorVia;
    /** For a PROPERTY anchor: which property, on what. The target addresses
     *  the object the property is reached through (that is the thing with an
     *  address) — without this the description would be about the object and
     *  never mention the property the chat was opened from. `ownerType` names
     *  the sub-object it actually lives on (a material, a geometry) when that
     *  is not the target itself. */
    readonly property?: { readonly key: string; readonly ownerType?: string };
    /** For an anchor opened by a context click IN THE 3D VIEWPORT: the raycast
     *  hit. `local` is the durable half (the object's own space — it stays
     *  meaningful when the object moves); `world` is where that was at click
     *  time; `parent` names what the object hangs under. See
     *  {@link AIChatAnchorHit}. */
    readonly hit?: AIChatAnchorHit;
}

/** Where a 3D context click actually landed on the anchored object. */
export type AIChatAnchorHit = {
    /** the hit point in the object's LOCAL space */
    readonly local: { readonly x: number; readonly y: number; readonly z: number };
    /** the hit point in WORLD space, at the time of the click */
    readonly world: { readonly x: number; readonly y: number; readonly z: number };
    /** name (or type) of the object's parent at the time of the click */
    readonly parent?: string;
}

/**
 * One open anchored-chat BUBBLE as it survives a reload
 * (plans/contextual-ask-ai.md step 7). Geometry is UI state, but it is kept
 * with the anchor→slug map for the same reason that map is: per HOST, MACHINE
 * scope — where a surface sits is a property of this browser, not the account.
 */
export type AIChatBubbleRecord = {
    /** the conversation this bubble drives */
    readonly slug: string;
    /** re-attached on restore, so the chat is about the thing again before the
     *  thing resolves (it may never resolve — then the bubble reads stale) */
    readonly anchor: AIChatAnchor;
    /** the user's drag offset from the anchor's screen point */
    readonly offset?: { x: number; y: number };
    /** where the card actually sat (client coords) — what a bubble whose anchor
     *  does NOT resolve after the reload comes back to; a resolving anchor uses
     *  {@link offset} instead and follows from there */
    readonly position?: { x: number; y: number };
    /** the user's resized card size, in px */
    readonly size?: { width: number; height: number };
    /** focus recency — highest is frontmost; the restore order comes from it */
    readonly raisedAt?: number;
    /** collapsed to the pill */
    readonly minimized?: boolean;
    /** the user dragged it somewhere deliberate: it STAYS there and stops
     *  following the anchor (the leader keeps pointing) */
    readonly pinned?: boolean;
    /** whether the conversation actually STARTED — an opened-and-abandoned
     *  bubble is a leftover, not state, and is never stored. Absent means
     *  "written before this was recorded": left alone rather than thrown away. */
    readonly hasMessages?: boolean;
}

export type AIGenerateTextResponse = {
    text: string;
}

export type AICompletionOptions = {
    /** Task instruction (the system prompt). Message + system are capped at
     * 32k chars by the server. */
    system?: string;
    /** Requested output ceiling in tokens; the server clamps it. */
    maxTokens?: number;
    token?: CancellationToken;
}

export interface IAISystemProviderWithTextGeneration extends IAISystemProvider {
    canGenerateText(): Promise<boolean>;
    generateText(prompt: string, options?: GenerateOptions): Promise<AIGenerateTextResponse>;
}
export function isAISystemProviderWithTextGeneration(provider: IAISystemProvider): provider is IAISystemProviderWithTextGeneration {
    return !!(provider.capabilities & AISystemProviderCapabilities.TextGeneration)
};

export interface IAISystemProviderWithCompletions extends IAISystemProvider {
    complete(prompt: string, options?: AICompletionOptions): Promise<AIGenerateTextResponse>;
}
export function isAISystemProviderWithCompletions(provider: IAISystemProvider): provider is IAISystemProviderWithCompletions {
    return !!(provider.capabilities & AISystemProviderCapabilities.Completions)
}

export interface IAISystemProviderWithTextGenerationStreaming extends IAISystemProvider {
    canGenerateText(): Promise<boolean>;
    generateTextStream(prompt: string, options?: GenerateOptions): AsyncIterable<AIGenerateTextResponse>;
}
export function isAISystemProviderWithTextGenerationStreaming(provider: IAISystemProvider): provider is IAISystemProviderWithTextGenerationStreaming {
    return !!(provider.capabilities & AISystemProviderCapabilities.TextGenerationStreaming)
}

export interface IAISystemProviderWithChatHistory extends IAISystemProvider {
    getChatHistory(chat?: string): Promise<Array<AIChatMessage>>;
    clearChatHistory(): Promise<void>;
}
export function isAISystemProviderWithChatHistory(provider: IAISystemProvider): provider is IAISystemProviderWithChatHistory {
    return !!(provider.capabilities & AISystemProviderCapabilities.ChatHistory)
}

// export interface IAISystemProviderWithContextAwareness extends IAISystemProvider {
//     // marker interface
// }
// export function isAISystemProviderWithContextAwareness(provider: IAISystemProvider): provider is IAISystemProviderWithContextAwareness {
//     return !!(provider.capabilities & AISystemProviderCapabilities.ContextAwareness)
// }










// #region AI Context


export const IAIContextService = createDecorator<IAIContextService>('IAIContextService');


export interface AIContextChangedEvent {
    provider: IAIContextProvider;
}

/**
 * Service to manage AI context providers
 */
export interface IAIContextService {

    readonly onDidChange: Event<AIContextChangedEvent>;

    registerProvider(provider: IAIContextProvider): void;
    getProviders(): Promise<Array<IAIContextProvider> | null>;


}

export enum AIContextProviderCapabilities {
    None = 0,
    Text = 1 << 0,
    Image = 1 << 1,
    File = 1 << 2,
}

/**
 * The AI context provider is meant to provide additional context to AI systems,
 * e.g. content of the currently viewed file or website, information about currently opened files (scripts, 3D models, etc.) or perhaps error messages
 */
export interface IAIContextProvider {

    /** Stable display name for debugging/telemetry (class names minify in builds). */
    readonly name?: string;

    capabilities: AIContextProviderCapabilities;

    /** Skip this provider's {@link getContext} for ANCHORED conversations —
     *  for chunks that are a per-message token tax the anchored bubble does
     *  not need (the scene overview: the anchor is the subject, and the model
     *  can still search the hierarchy with its tools). */
    readonly omitForAnchoredChats?: boolean;

    getContext(): Promise<AITextContextChunk[] | null>;

    /**
     * Describe an anchored conversation's anchor — called per turn (fresh, live
     * values), ONLY for conversations that have one, IN ADDITION to
     * {@link getContext}. Resolve the target by address at call time; if it no
     * longer resolves, say so rather than failing (the attribution must
     * degrade, never break).
     */
    /**
     * Describe what an anchored conversation is ABOUT, fresh for this turn.
     *
     * The WHOLE anchor, not just its target: `kind` is open-ended (anything in
     * a project can be an anchor — objects and resources today, files, graph
     * nodes and properties as their addressing lands), so a provider has to be
     * able to see what it is being asked about, answer for the kinds it knows
     * and return null for the rest. Several providers may answer; their text
     * is concatenated.
     */
    getAnchorContext?(anchor: AIChatAnchor): Promise<AITextContextChunk[] | null>;

}

/**
 * A chunk of text context provided by an AI context provider
 */
export type AITextContextChunk = {
    type: 'text',
    text: string
} | {
    type: "image",
    data: Uint8Array | string,
} | {
    type: "file",
    name: string,
    path: string,
    size: number,
}


// #endregion

// #region AI tools

export const IAIToolService = createDecorator<IAIToolService>('IAIToolService');

export interface AIToolServiceChangedEvent {
    readonly service: IAIToolService;
}
/** WHO is calling a tool — threaded from the chat turn (#138). Absent on
 *  surfaces without a conversation (MCP, agent), so a tool that needs one
 *  must handle undefined by refusing, not guessing. */
export interface AIToolCallContext {
    readonly conversation?: { readonly slug: string };
}

export interface IAIToolService {

    readonly onDidChange: Event<AIToolServiceChangedEvent>;

    registerTool(tool: IAITool): void;
    getAvailableTools(): Promise<IAITool[]>;
    callTool(name: string, parameters: object, context?: AIToolCallContext): Promise<AIToolCallResponse>;
}

export interface AIToolCallResponse {
    result: {
        success: boolean;
        message?: string;
        /**
         * Optional AI message content returned by the tool. When present, this message can be directly used as a response to the AI user instead of further processing the tool response.
         */
        aiMessage?: string;
        /**
         * Pictures the tool produced, as data or http urls — NOT pasted into
         * `aiMessage`. A data url in the text is re-sent with the history every
         * turn and cannot be looked at: one 512x512 screenshot measured ~109,000
         * characters of base64 for zero sight.
         */
        aiImages?: Array<string | AIToolImage>;
    }
}

export interface AIToolImage {
    url: string;
    /** `low` costs a fraction of `high` and answers most of what a screenshot is asked. */
    detail?: "high" | "low";
}
export function isAIToolCallResponse(obj: any): obj is AIToolCallResponse {
    return obj && typeof obj === "object" && "result" in obj && typeof obj.result === "object" && "success" in obj.result;
}

/**
 * What a tool TOUCHES (#127): `scene` = the inspected scene (safe to offer on
 * any page), `local` = project/machine files, `account` = the cloud account.
 * The server withholds local/account tools from chats on untrusted origins.
 */
export type AIToolCapability = "scene" | "local" | "account";

export interface IAITool<T extends unknown = unknown> {
    readonly type: "function";

    name: string;
    description: string;
    parametersJSONSchema: IJSONSchema;
    allowMcp?: boolean;
    /** see {@link AIToolCapability}; unset = treated as `scene` at the wire */
    capability?: AIToolCapability;
    /**
     * Context-key expression gating WHERE this tool is offered (#138) —
     * evaluated per surface via `toolAvailability.ts` with the surface's
     * overlay keys (e.g. `aiChat.anchored` for a conversation). FAILS CLOSED
     * on surfaces that cannot satisfy it (MCP has no conversation). Unset =
     * available everywhere.
     */
    when?: ContextKeyExpression;

    // canRun?(): Promise<boolean>;
    call(parameters?: T, context?: AIToolCallContext): Promise<AIToolCallResponse>;

    canRun?(): boolean;
}

export abstract class FunctionAiTool<T extends unknown = unknown> implements IAITool<T> {
    readonly type = "function";
    abstract name: string;
    abstract description: string;
    abstract parametersJSONSchema: IJSONSchema;
    readonly allowMcp?: boolean;
    readonly capability?: AIToolCapability;
    readonly when?: ContextKeyExpression;
    abstract call(parameters?: T, context?: AIToolCallContext): Promise<AIToolCallResponse>;
    canRun?(): boolean;
}
